import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from 'react';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogActions from '@material-ui/core/DialogActions';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { KeyboardDatePicker } from '@material-ui/pickers';
import moment from 'moment';
import { useSnackbar } from 'notistack';

import {
  api,
  createAPI,
  getListAPI,
  getUserId,
  updateAPI,
} from '../../services';
import { STATUS_ATIVO, formatDateDDMMYYYY } from '../../utils';

const styles = {
  root: {
    margin: 0,
    padding: '16px',
  },
  closeButton: {
    position: 'absolute',
    right: '8px',
    top: '8px',
    color: 'black',
  },
};

const DialogTitle = (props) => {
  const { children, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography style={styles.root} {...other}>
      <Typography variant="h6" style={{ fontWeight: 'bold' }}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          style={styles.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
};

const DEFAULT_CONTEXTO = { endereco_exposicao_produto_id: null, andar: 1 };

const DialogoAdicionarProduto = forwardRef(
  ({ handleClose = () => {} }, ref) => {
    const [open, setOpen] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const [contexto, setContexto] = useState(DEFAULT_CONTEXTO);
    const [produto, setProduto] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [valueDebounce, setValueDebounce] = useState('');
    const [options, setOptions] = useState([]);
    const [carregando, setCarregando] = useState(false);

    const [validades, setValidades] = useState([]);
    const [validadeAtual, setValidadeAtual] = useState(new Date());
    const [modoEdicao, setModoEdicao] = useState(false);
    const [andarProdutoId, setAndarProdutoId] = useState(null);

    const handleCloseDialog = () => {
      setOpen(false);
      setProduto(null);
      setInputValue('');
      setContexto(DEFAULT_CONTEXTO);
      setValidades([]);
      setValidadeAtual(new Date());
      setModoEdicao(false);
      setAndarProdutoId(null);
    };

    useImperativeHandle(ref, () => ({
      handleOpen(contextoLocal) {
        setContexto(contextoLocal);
        const existente = contextoLocal.andarProdutoExistente;
        if (existente) {
          setModoEdicao(true);
          setAndarProdutoId(existente.id);
          setProduto(existente.produto || null);
          setInputValue(existente.produto ? existente.produto.nome : '');
          setValidades(
            (existente.validades || []).map((validade) => ({
              id: validade.id,
              validade: validade.validade,
            })),
          );
        } else {
          setModoEdicao(false);
          setAndarProdutoId(null);
        }
        setOpen(true);
      },
    }));

    useEffect(() => {
      const timer = setTimeout(() => setValueDebounce(inputValue), 500);
      return () => clearTimeout(timer);
    }, [inputValue]);

    useEffect(() => {
      let active = true;

      (async () => {
        const { data } = await getListAPI(
          'produtos_search_filter',
          ['id', 'asc'],
          [1, 10],
          {
            nome: valueDebounce,
            status: STATUS_ATIVO,
          },
          [],
        );
        if (active) setOptions(data);
      })();

      return () => {
        active = false;
      };
    }, [valueDebounce]);

    function handleAdicionarValidade() {
      const validadeFormatada = moment(validadeAtual).format('YYYY-MM-DD');
      const hojeFormatado = moment().format('YYYY-MM-DD');

      if (validadeFormatada < hojeFormatado) {
        enqueueSnackbar('A validade não pode ser anterior a hoje!', {
          variant: 'error',
        });
        return;
      }

      if (validades.some((item) => item.validade === validadeFormatada)) {
        enqueueSnackbar('Esta validade já foi adicionada!', {
          variant: 'error',
        });
        return;
      }
      setValidades((prev) => [
        ...prev,
        { id: null, validade: validadeFormatada },
      ]);
    }

    async function handleRemoverValidade(index) {
      const item = validades[index];
      if (item.id) {
        try {
          await api.put(
            `/enderecos_exposicao_andares_produtos_validades/status/inativar/${item.id}`,
          );
        } catch (erros) {
          enqueueSnackbar(`${erros}`, { variant: 'error' });
          return;
        }
      }
      setValidades((prev) => prev.filter((_, i) => i !== index));
    }

    async function handleSalvar() {
      if (!produto) return;
      setCarregando(true);
      try {
        let idAndarProduto = andarProdutoId;

        if (modoEdicao) {
          const response = await updateAPI(
            'enderecos_exposicao_andares_produtos',
            andarProdutoId,
            { produto_id: produto.id },
          );
          if (!response.data.status) {
            setCarregando(false);
            enqueueSnackbar(response.data.message, { variant: 'error' });
            return;
          }
        } else {
          const response = await createAPI(
            'enderecos_exposicao_andares_produtos',
            {
              endereco_exposicao_produto_id:
                contexto.endereco_exposicao_produto_id,
              andar: contexto.andar,
              produto_id: produto.id,
              user_id: getUserId(),
            },
          );
          if (!response.data.status) {
            setCarregando(false);
            enqueueSnackbar(response.data.message, { variant: 'error' });
            return;
          }
          idAndarProduto = response.data.data.id;
        }

        const validadesNovas = validades.filter((item) => !item.id);
        if (validadesNovas.length > 0) {
          await Promise.all(
            validadesNovas.map((item) =>
              createAPI('enderecos_exposicao_andares_produtos_validades', {
                endereco_exposicao_andar_produto_id: idAndarProduto,
                validade: item.validade,
              }),
            ),
          );
        }

        setCarregando(false);
        enqueueSnackbar(
          modoEdicao ? 'Produto atualizado!' : 'Produto adicionado ao andar!',
          { variant: 'success' },
        );
        handleCloseDialog();
        handleClose();
      } catch (erros) {
        setCarregando(false);
        enqueueSnackbar(`${erros}`, { variant: 'error' });
      }
    }

    return (
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title" onClose={handleCloseDialog}>
          {modoEdicao ? 'Editar Produto' : 'Adicionar Produto'}
        </DialogTitle>
        <MuiDialogContent>
          <Autocomplete
            value={produto}
            onChange={(event, newValue) => setProduto(newValue)}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) =>
              setInputValue(newInputValue)
            }
            getOptionSelected={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.nome || ''}
            options={options}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                margin="normal"
                label="Produto"
                autoFocus
                fullWidth
              />
            )}
          />

          <Box display="flex" alignItems="flex-start" mt="1em">
            <Box flex={1} mr="0.5em">
              <KeyboardDatePicker
                fullWidth
                format="dd/MM/yyyy"
                label="Validade"
                value={validadeAtual}
                onChange={setValidadeAtual}
                inputVariant="outlined"
                disablePast
                autoOk
              />
            </Box>
            <Tooltip title="Adicionar Validade">
              <IconButton
                onClick={handleAdicionarValidade}
                style={{
                  marginTop: '8px',
                  backgroundColor: '#1976d2',
                  color: '#fff',
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {validades.length > 0 ? (
            <Box display="flex" flexWrap="wrap" mt="1em" style={{ gap: '8px' }}>
              {validades.map((item, index) => (
                <Chip
                  key={item.id || item.validade}
                  size="small"
                  label={formatDateDDMMYYYY(item.validade)}
                  onDelete={() => handleRemoverValidade(index)}
                />
              ))}
            </Box>
          ) : null}
        </MuiDialogContent>
        <MuiDialogActions style={{ padding: '16px' }}>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSalvar}
            disabled={!produto || carregando}
          >
            {carregando ? (
              <CircularProgress size={20} />
            ) : modoEdicao ? (
              'Salvar'
            ) : (
              'Adicionar'
            )}
          </Button>
        </MuiDialogActions>
      </Dialog>
    );
  },
);

export default DialogoAdicionarProduto;
